import { array, object, string } from 'yup';
import { AniPayFormData } from 'app/pages/anipay/model/types';
import i18n from 'app/utils/translation/i18n';
import { formatNumber } from '../alias/add-alias-modal/add-alias-modal';

const mobileRegex = /^[+]?[0-9]{13}$/;
const innRegex = /^[0-9]{10,12}$/;
const pinRegex = /^[0-9]{7}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const AniPaySchema = object<AniPayFormData>().shape({
  bankCode: string().notRequired(),
  aliasType: object({
    value: string().required(i18n.t('anipay.schema_aliasType')),
    label: string().required(i18n.t('anipay.schema_aliasType')),
  }).required(i18n.t('anipay.schema_aliasType')),
  aliasValue: string()
    .required(i18n.t('anipay.schema_aliasValue'))
    .when('aliasType.value', {
      is: 'MOBILE',
      then: (schema) =>
        schema
          .required(i18n.t('anipay.schema_phoneNumber_required'))
          .test('valid-phone', i18n.t('anipay.schema_phoneNumber_min_9_symbols'), (val) => {
            return (formatNumber(val)?.length ?? 0) >= 9;
          }),
    })
    .when('aliasType.value', {
      is: 'EMAIL',
      then: (schema) =>
        schema
          .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, i18n.t('anipay.schema_wrong_email_format'))
          .min(5, i18n.t('anipay.schema_email_min_5_symbols'))
          .max(50, i18n.t('anipay.schema_email_max_50_symbols'))
          .required(i18n.t('anipay.schema_email_required')),
    })
    .when('aliasType.value', {
      is: 'TIN',
      then: (schema) =>
        schema
          .matches(/^[0-9]+$/, i18n.t('anipay.schema_tin_only_numbers'))
          .length(10, i18n.t('anipay.schema_tin_min_10_symbols'))
          .required(i18n.t('anipay.schema_tin_required')),
    })
    .when('aliasType.value', {
      is: 'PIN',
      then: (schema) =>
        schema.transform((value) => value?.toUpperCase())
          .matches(/^[A-Z0-9]+$/, i18n.t('anipay.schema_only_uppercase_english_letters'))
          .min(5, i18n.t('anipay.schema_pin_min_5_symbols'))
          .max(7, i18n.t('anipay.schema_pin_max_7_symbols'))
          .required(i18n.t('anipay.schema_pin_required')),
    }),
  bank_name: string(),
  cr_acc_no: string(),
  cr_name: string(),
  cr_tax_no: string(),
  cr_type_id: string().notRequired(),
  bank_account: string(),
  contract_no: string().required(i18n.t('anipay.schema_contract_account_number_required')),
  description: string()
    .required(i18n.t('anipay.schema_payment_assignment_required'))
    .matches(/^[a-zA-ZəöğçşüƏÖĞÇŞÜ\s.,!?-]*$/, i18n.t('anipay.schema_only_az_eng_letters')),
  amount: string()
    .matches(/^(\d+\.?\d*|\.\d+)$/, i18n.t('anipay.schema_only_numbers'))
    .required(i18n.t('anipay.schema_amount_required')),
  account: object({
    value: string().required(i18n.t('anipay.schema_outflow_account_required')),
    label: string().required(i18n.t('anipay.schema_outflow_account_required')),
  }).required(i18n.t('anipay.schema_outflow_account_required')),
  comm_account: object({
    value: string().required(i18n.t('anipay.schema_commission_outflow_account_required')),
    label: string().required(i18n.t('anipay.schema_commission_outflow_account_required')),
  }).required(i18n.t('anipay.schema_commission_outflow_account_required')),
  files: array()
    .of(
      object({
        file_id: string(),
        name: string(),
        size: string(),
      })
    )
    .when('cr_type_id', {
      is: (cr_type_id: string) => cr_type_id === '3',
      then: (schema) => schema.required(i18n.t('anipay.schema_file_is_not_selected')),
    }),
});
